import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  Users, 
  Package, 
  DollarSign,
  Star,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { formatCurrency } from '../../utils';

interface AnalyticsData {
  revenue: {
    total: number;
    monthly: number;
    weekly: number;
    daily: number;
    growth: number;
    trend: 'up' | 'down' | 'stable';
  };
  orders: {
    total: number;
    completed: number;
    pending: number;
    cancelled: number;
    in_progress: number;
    average_value: number;
    completion_rate: number;
  };
  customers: {
    total: number;
    new_this_month: number;
    repeat_customers: number;
    average_orders: number;
    retention_rate: number;
  };
  performance: {
    average_completion_time: number;
    customer_satisfaction: number;
    on_time_delivery: number;
    top_garments: Array<{ name: string; orders: number; revenue: number }>;
    peak_hours: Array<{ hour: string; orders: number }>;
  };
  inventory: {
    total_items: number;
    low_stock_items: number;
    total_value: number;
    turnover_rate: number;
  };
}

interface TimeSeriesData {
  labels: string[];
  revenue: number[];
  orders: number[];
  customers: number[];
}

const BusinessAnalytics: React.FC = () => {
  const { user, token } = useAuth();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod]);

  const loadAnalyticsData = async () => {
    if (!token) return;

    setIsLoading(true);
    setError('');

    try {
      // Load orders data
      const ordersResponse = await fetch(`${API_BASE}/api/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Load customers data
      const customersResponse = await fetch(`${API_BASE}/api/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Load inventory data
      const inventoryResponse = await fetch(`${API_BASE}/api/tailors/inventory`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (ordersResponse.ok && customersResponse.ok && inventoryResponse.ok) {
        const orders = await ordersResponse.json();
        const customers = await customersResponse.json();
        const inventory = await inventoryResponse.json();

        // Process the data
        const processedData = processAnalyticsData(orders, customers, inventory);
        setAnalyticsData(processedData);
        
        // Generate time series data
        const timeSeries = generateTimeSeriesData(orders, selectedPeriod);
        setTimeSeriesData(timeSeries);
      } else {
        setError('Failed to load analytics data');
      }
    } catch (_error) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const processAnalyticsData = (orders: any, customers: any, inventory: any): AnalyticsData => {
    const ordersList = orders.orders || orders || [];
    const customersList = customers.customers || customers || [];
    const inventoryItems = inventory.items || [];

    // Calculate revenue metrics
    const totalRevenue = ordersList.reduce((sum: number, order: any) => sum + (order.amount || 0), 0);
    const completedOrders = ordersList.filter((order: any) => order.status === 'completed');
    const completedRevenue = completedOrders.reduce((sum: number, order: any) => sum + (order.amount || 0), 0);
    
    // Calculate time-based metrics
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const monthlyOrders = ordersList.filter((order: any) => 
      new Date(order.created_at) >= thisMonth
    );
    const weeklyOrders = ordersList.filter((order: any) => 
      new Date(order.created_at) >= thisWeek
    );
    const dailyOrders = ordersList.filter((order: any) => 
      new Date(order.created_at) >= thisDay
    );

    const monthlyRevenue = monthlyOrders.reduce((sum: number, order: any) => sum + (order.amount || 0), 0);
    const weeklyRevenue = weeklyOrders.reduce((sum: number, order: any) => sum + (order.amount || 0), 0);
    const dailyRevenue = dailyOrders.reduce((sum: number, order: any) => sum + (order.amount || 0), 0);

    // Calculate growth (simplified - compare with previous period)
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthOrders = ordersList.filter((order: any) => {
      const orderDate = new Date(order.created_at);
      return orderDate >= lastMonth && orderDate < thisMonth;
    });
    const lastMonthRevenue = lastMonthOrders.reduce((sum: number, order: any) => sum + (order.amount || 0), 0);
    const growth = lastMonthRevenue > 0 ? ((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 : 0;

    // Calculate order metrics
    const pendingOrders = ordersList.filter((order: any) => order.status === 'pending').length;
    const inProgressOrders = ordersList.filter((order: any) => order.status === 'in_progress').length;
    const cancelledOrders = ordersList.filter((order: any) => order.status === 'cancelled').length;
    const averageOrderValue = ordersList.length > 0 ? totalRevenue / ordersList.length : 0;
    const completionRate = ordersList.length > 0 ? (completedOrders.length / ordersList.length) * 100 : 0;

    // Calculate customer metrics
    const newCustomersThisMonth = customersList.filter((customer: any) => 
      new Date(customer.created_at) >= thisMonth
    ).length;
    const repeatCustomers = customersList.filter((customer: any) => {
      const customerOrders = ordersList.filter((order: any) => order.customer_id === customer.id);
      return customerOrders.length > 1;
    }).length;
    const averageOrdersPerCustomer = customersList.length > 0 ? ordersList.length / customersList.length : 0;
    const retentionRate = customersList.length > 0 ? (repeatCustomers / customersList.length) * 100 : 0;

    // Calculate performance metrics
    const completedOrdersWithDates = completedOrders.filter((order: any) => order.created_at && order.updated_at);
    const averageCompletionTime = completedOrdersWithDates.length > 0 
      ? completedOrdersWithDates.reduce((sum: number, order: any) => {
          const created = new Date(order.created_at);
          const completed = new Date(order.updated_at);
          return sum + (completed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24); // days
        }, 0) / completedOrdersWithDates.length
      : 0;

    // Top garments analysis
    const garmentStats: { [key: string]: { orders: number; revenue: number } } = {};
    ordersList.forEach((order: any) => {
      const garmentType = order.garment_type || order.order_type || 'Unknown';
      if (!garmentStats[garmentType]) {
        garmentStats[garmentType] = { orders: 0, revenue: 0 };
      }
      garmentStats[garmentType].orders++;
      garmentStats[garmentType].revenue += order.amount || 0;
    });

    const topGarments = Object.entries(garmentStats)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5);

    // Peak hours analysis (simplified)
    const hourStats: { [key: string]: number } = {};
    ordersList.forEach((order: any) => {
      const hour = new Date(order.created_at).getHours();
      const hourKey = `${hour}:00`;
      hourStats[hourKey] = (hourStats[hourKey] || 0) + 1;
    });

    const peakHours = Object.entries(hourStats)
      .map(([hour, orders]) => ({ hour, orders }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 4);

    // Inventory metrics
    const totalInventoryValue = inventoryItems.reduce((sum: number, item: any) => 
      sum + ((item.cost_per_unit || 0) * (item.current_stock || 0)), 0);
    const lowStockItems = inventoryItems.filter((item: any) => 
      item.current_stock <= item.minimum_stock).length;

    return {
      revenue: {
        total: totalRevenue,
        monthly: monthlyRevenue,
        weekly: weeklyRevenue,
        daily: dailyRevenue,
        growth: growth,
        trend: growth > 5 ? 'up' : growth < -5 ? 'down' : 'stable'
      },
      orders: {
        total: ordersList.length,
        completed: completedOrders.length,
        pending: pendingOrders,
        cancelled: cancelledOrders,
        in_progress: inProgressOrders,
        average_value: averageOrderValue,
        completion_rate: completionRate
      },
      customers: {
        total: customersList.length,
        new_this_month: newCustomersThisMonth,
        repeat_customers: repeatCustomers,
        average_orders: averageOrdersPerCustomer,
        retention_rate: retentionRate
      },
      performance: {
        average_completion_time: averageCompletionTime,
        customer_satisfaction: 4.5, // Mock data - would come from reviews
        on_time_delivery: 85, // Mock data - would be calculated from due dates
        top_garments: topGarments,
        peak_hours: peakHours
      },
      inventory: {
        total_items: inventoryItems.length,
        low_stock_items: lowStockItems,
        total_value: totalInventoryValue,
        turnover_rate: 2.5 // Mock data - would be calculated from usage patterns
      }
    };
  };

  const generateTimeSeriesData = (orders: any, period: string): TimeSeriesData => {
    const ordersList = orders.orders || orders || [];
    const now = new Date();
    
    const labels: string[] = [];
    const revenue: number[] = [];
    const ordersCount: number[] = [];
    const customers: number[] = [];

    if (period === 'week') {
      // Last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
        
        const dayOrders = ordersList.filter((order: any) => {
          const orderDate = new Date(order.created_at);
          return orderDate >= dayStart && orderDate < dayEnd;
        });
        
        labels.push(date.toLocaleDateString('en-US', { weekday: 'short' }));
        revenue.push(dayOrders.reduce((sum: number, order: any) => sum + (order.amount || 0), 0));
        ordersCount.push(dayOrders.length);
        customers.push(new Set(dayOrders.map((order: any) => order.customer_id)).size);
      }
    } else if (period === 'month') {
      // Last 30 days
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
        
        const dayOrders = ordersList.filter((order: any) => {
          const orderDate = new Date(order.created_at);
          return orderDate >= dayStart && orderDate < dayEnd;
        });
        
        labels.push(date.getDate().toString());
        revenue.push(dayOrders.reduce((sum: number, order: any) => sum + (order.amount || 0), 0));
        ordersCount.push(dayOrders.length);
        customers.push(new Set(dayOrders.map((order: any) => order.customer_id)).size);
      }
    }

    return { labels, revenue, orders: ordersCount, customers };
  };

  const renderMetricCard = (
    title: string, 
    value: string | number, 
    subtitle: string, 
    icon: React.ReactNode, 
    trend?: 'up' | 'down' | 'stable',
    growth?: number
  ) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
          <p className="text-sm text-gray-500 mb-2">{subtitle}</p>
          {growth !== undefined && (
            <div className="flex items-center gap-1">
              {trend === 'up' && <ArrowUpRight className="w-4 h-4 text-green-600" />}
              {trend === 'down' && <ArrowDownRight className="w-4 h-4 text-red-600" />}
              <span className={`text-sm font-medium ${
                trend === 'up' ? 'text-green-600' : 
                trend === 'down' ? 'text-red-600' : 
                'text-gray-600'
              }`}>
                {growth > 0 ? '+' : ''}{growth.toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        <div className="text-3xl text-gray-400">
          {icon}
        </div>
      </div>
    </motion.div>
  );

  const renderChart = (title: string, data: TimeSeriesData | null, type: 'revenue' | 'orders' | 'customers') => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex space-x-2">
          {(['week', 'month', 'quarter', 'year'] as const).map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                selectedPeriod === period
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      {data ? (
        <div className="h-64 bg-gray-50 rounded-lg p-4">
          <div className="h-full flex items-end justify-between gap-2">
            {data[type].map((value, index) => {
              const maxValue = Math.max(...data[type]);
              const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
              
              return (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div 
                    className="bg-blue-500 rounded-t w-full transition-all duration-300 hover:bg-blue-600"
                    style={{ height: `${height}%` }}
                    title={`${data.labels[index]}: ${value}`}
                  />
                  <span className="text-xs text-gray-600 mt-2">{data.labels[index]}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600">No data available</p>
          </div>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading analytics data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <div className="text-red-600">⚠️</div>
              <div>
                <h3 className="text-lg font-medium text-red-800">Error Loading Analytics</h3>
                <p className="text-red-700">{error}</p>
                <button
                  onClick={loadAnalyticsData}
                  className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-16">
            <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Analytics Data</h2>
            <p className="text-gray-600">Start creating orders to see your analytics dashboard</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-gray-600 mt-1">Track your business performance and growth</p>
            </div>
            <button
              onClick={loadAnalyticsData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {renderMetricCard(
            'Total Revenue',
            formatCurrency(analyticsData.revenue.total),
            'All time',
            <DollarSign className="w-8 h-8" />,
            analyticsData.revenue.trend,
            analyticsData.revenue.growth
          )}
          
          {renderMetricCard(
            'Total Orders',
            analyticsData.orders.total,
            `${analyticsData.orders.completed} completed`,
            <Package className="w-8 h-8" />,
            'stable'
          )}
          
          {renderMetricCard(
            'Total Customers',
            analyticsData.customers.total,
            `${analyticsData.customers.new_this_month} new this month`,
            <Users className="w-8 h-8" />,
            'stable'
          )}
          
          {renderMetricCard(
            'Completion Rate',
            `${analyticsData.orders.completion_rate.toFixed(1)}%`,
            'Orders completed',
            <Target className="w-8 h-8" />,
            'stable'
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {renderChart('Revenue Trend', timeSeriesData, 'revenue')}
          {renderChart('Orders Trend', timeSeriesData, 'orders')}
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Top Garments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Garments</h3>
            <div className="space-y-3">
              {analyticsData.performance.top_garments.map((garment, index) => (
                <div key={garment.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{garment.name}</p>
                      <p className="text-sm text-gray-600">{garment.orders} orders</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{formatCurrency(garment.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700">Completed</span>
                </div>
                <span className="font-medium">{analyticsData.orders.completed}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">In Progress</span>
                </div>
                <span className="font-medium">{analyticsData.orders.in_progress}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-700">Pending</span>
                </div>
                <span className="font-medium">{analyticsData.orders.pending}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-gray-700">Cancelled</span>
                </div>
                <span className="font-medium">{analyticsData.orders.cancelled}</span>
              </div>
            </div>
          </div>

          {/* Performance Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Avg. Completion Time</span>
                <span className="font-medium">{analyticsData.performance.average_completion_time.toFixed(1)} days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Customer Satisfaction</span>
                <span className="font-medium flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  {analyticsData.performance.customer_satisfaction}/5
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">On-time Delivery</span>
                <span className="font-medium">{analyticsData.performance.on_time_delivery}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Customer Retention</span>
                <span className="font-medium">{analyticsData.customers.retention_rate.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Overview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{analyticsData.inventory.total_items}</p>
              <p className="text-sm text-gray-600">Total Items</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{analyticsData.inventory.low_stock_items}</p>
              <p className="text-sm text-gray-600">Low Stock</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(analyticsData.inventory.total_value)}</p>
              <p className="text-sm text-gray-600">Total Value</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{analyticsData.inventory.turnover_rate}x</p>
              <p className="text-sm text-gray-600">Turnover Rate</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAnalytics;
